/* eslint-disable react/no-unknown-property */
import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js";
import { EffectComposer, Bloom, ChromaticAberration, Noise, Vignette } from "@react-three/postprocessing";
import { BlendFunction } from "postprocessing";
import * as THREE from "three";
import { useLenis } from "lenis/react";

/* ============================================================= *
 *  PRESTIGE WELLNESS CO — cinematic WebGL experience
 *  SIDEWAVE-style 5-state scroll journey, neon/sunset palette
 * ============================================================= */

/* ---- product words (kept from Prestige brand) ---- */
const CAROUSEL = ["RADIANCE", "COLLAGEN", "VITALITY", "GLOW"];

/* ---- per-state colour palettes (5 states) — brighter mix ---- */
const SKY_TOP = ["#5a1d8a", "#7830a8", "#ff8a4a", "#8a5ac8", "#c0409a"].map((c) => new THREE.Color(c));
const SKY_BOT = ["#ff6ab0", "#ff4d9e", "#ffe07a", "#ffb0e0", "#ffb078"].map((c) => new THREE.Color(c));
const TERRAIN = ["#4a1a55", "#5a2065", "#a04030", "#4a2f7a", "#6a2860"].map((c) => new THREE.Color(c));
const FOGCOL = ["#8a3a90", "#a03a8a", "#ffb07a", "#9a7ac8", "#c05a95"].map((c) => new THREE.Color(c));
const STARCOL = ["#ff8fe5", "#ff5fb0", "#ffe07a", "#a0c8ff", "#ffb0e0"].map((c) => new THREE.Color(c));
const SUN_OPACITY = [0.2, 0.4, 1.0, 0.55, 0.3];

function lerpPalette(arr, p, out) {
  const n = arr.length - 1;
  const x = THREE.MathUtils.clamp(p, 0, 1) * n;
  const i = Math.min(n - 1, Math.floor(x));
  const t = x - i;
  return out.copy(arr[i]).lerp(arr[i + 1], t);
}
function lerpScalar(arr, p) {
  const n = arr.length - 1;
  const x = THREE.MathUtils.clamp(p, 0, 1) * n;
  const i = Math.min(n - 1, Math.floor(x));
  return THREE.MathUtils.lerp(arr[i], arr[i + 1], x - i);
}

/* ---- 360° orbit keyframes: (radius, camY, lookY) through the 5 SIDEWAVE states ---- */
const ORBIT_KF = [
  { r: 15.0, py: 3.4,  ly: 2.6 },  // S1 INTRO   — wide establish
  { r: 6.0,  py: 3.15, ly: 3.15 }, // S2 TUNNEL  — closer, at head height
  { r: 11.0, py: 3.6,  ly: 2.7 },  // S3 SUNSET  — pull back, horizon
  { r: 10.5, py: 3.3,  ly: 2.7 },  // S4 REVEAL  — slight glitch offset
  { r: 4.2,  py: 2.7,  ly: 2.7 },  // S5 INDEX   — torso macro close-up
];
// how many full orbits over the scroll (1.0 = 360°)
const ORBIT_TURNS = 1.0;

/* ================= Sky dome (nebula + procedural stars) ================= */
function SkyDome({ pRef }) {
  const mat = useRef();
  const uniforms = useMemo(
    () => ({
      uTop: { value: new THREE.Color("#2a0940") },
      uBot: { value: new THREE.Color("#7a1450") },
      uTime: { value: 0 },
      uStar: { value: new THREE.Color("#ff4fd8") },
    }),
    []
  );
  useFrame((s) => {
    uniforms.uTime.value = s.clock.elapsedTime;
    lerpPalette(SKY_TOP, pRef.current, uniforms.uTop.value);
    lerpPalette(SKY_BOT, pRef.current, uniforms.uBot.value);
    lerpPalette(STARCOL, pRef.current, uniforms.uStar.value);
  });
  return (
    <mesh scale={[-1, 1, 1]}>
      <sphereGeometry args={[300, 64, 64]} />
      <shaderMaterial
        ref={mat}
        side={THREE.BackSide}
        uniforms={uniforms}
        vertexShader={`varying vec3 vP; void main(){ vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);} `}
        fragmentShader={`
          varying vec3 vP;
          uniform vec3 uTop; uniform vec3 uBot; uniform vec3 uStar;
          uniform float uTime;
          float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453123); }
          float noise(vec2 p){
            vec2 i = floor(p); vec2 f = fract(p);
            float a = hash(i);
            float b = hash(i + vec2(1.0,0.0));
            float c = hash(i + vec2(0.0,1.0));
            float d = hash(i + vec2(1.0,1.0));
            vec2 u = f*f*(3.0-2.0*f);
            return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
          }
          float fbm(vec2 p){
            float v = 0.0; float a = 0.5;
            for (int i = 0; i < 5; i++){ v += a * noise(p); p *= 2.02; a *= 0.5; }
            return v;
          }
          void main(){
            vec3 dir = normalize(vP);
            float h = clamp(dir.y*0.5 + 0.5, 0.0, 1.0);
            vec3 c = mix(uBot, uTop, pow(h, 0.8));
            // spherical uv-ish coords for procedural detail
            vec2 uv = vec2(atan(dir.z, dir.x)/6.2831853 + 0.5, dir.y*0.5 + 0.5);
            // nebula swirls (only in upper sky)
            float neb = fbm(uv * vec2(3.5, 2.0) + vec2(uTime*0.006, 0.0));
            neb = smoothstep(0.45, 0.85, neb) * smoothstep(0.35, 0.75, h);
            c += uStar * neb * 0.35;
            // procedural stars (bright pinpoints)
            vec2 sp = floor(uv * vec2(700.0, 420.0));
            float r = hash(sp);
            float star = smoothstep(0.9965, 1.0, r);
            float tw = 0.55 + 0.45 * sin(uTime*2.0 + r*40.0);
            c += vec3(1.0) * star * tw * smoothstep(0.28, 0.6, h) * 1.6;
            // horizon haze (warm glow near horizon)
            c += uStar * 0.12 * (1.0 - smoothstep(0.4, 0.62, h));
            gl_FragColor = vec4(c, 1.0);
          } `}
      />
    </mesh>
  );
}

/* ================= Distant mountain silhouettes (parallax layers) ================= */
function DistantMountains({ pRef }) {
  const layers = useMemo(
    () => [
      { z: -110, y: -0.2, amp: 8.0, freq: 0.05, w: 260, alpha: 0.55, tone: 0.35 },
      { z: -80, y: -0.6, amp: 6.2, freq: 0.075, w: 220, alpha: 0.72, tone: 0.22 },
      { z: -55, y: -1.0, amp: 4.6, freq: 0.11, w: 190, alpha: 0.88, tone: 0.12 },
    ],
    []
  );
  return (
    <group>
      {layers.map((L, i) => (
        <MountainLayer key={i} pRef={pRef} config={L} seed={i * 31.3} />
      ))}
    </group>
  );
}
function MountainLayer({ pRef, config, seed }) {
  const mat = useRef();
  const uniforms = useMemo(
    () => ({
      uColor: { value: new THREE.Color("#1a0620") },
      uAmp: { value: config.amp },
      uFreq: { value: config.freq },
      uSeed: { value: seed },
      uTone: { value: config.tone },
    }),
    [config, seed]
  );
  const geo = useMemo(() => new THREE.PlaneGeometry(config.w, 22, 220, 1), [config]);
  useFrame(() => {
    if (mat.current) {
      lerpPalette(TERRAIN, pRef.current, uniforms.uColor.value);
      uniforms.uColor.value.multiplyScalar(0.55 + uniforms.uTone.value);
    }
  });
  return (
    <mesh geometry={geo} position={[0, config.y, config.z]}>
      <shaderMaterial
        ref={mat}
        transparent
        depthWrite={false}
        side={THREE.DoubleSide}
        uniforms={{ ...uniforms, uAlpha: { value: config.alpha } }}
        vertexShader={`
          varying vec2 vUv;
          uniform float uAmp, uFreq, uSeed;
          float hash(float n){ return fract(sin(n)*43758.5453123); }
          float noise(float x){ float i = floor(x); float f = fract(x); float a = hash(i); float b = hash(i+1.0); return mix(a,b,f*f*(3.0-2.0*f)); }
          void main(){
            vUv = uv;
            vec3 p = position;
            float x = p.x * uFreq + uSeed;
            float h = noise(x)*0.7 + noise(x*2.1)*0.22 + noise(x*4.7)*0.08;
            // silhouette only affects top edge (uv.y = 1)
            p.y += uv.y * (h - 0.5) * uAmp * 2.0;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
          } `}
        fragmentShader={`
          varying vec2 vUv;
          uniform vec3 uColor;
          uniform float uAlpha;
          void main(){
            // hard silhouette: pass only where uv.y < 0.5-ish (bottom half of quad)
            if (vUv.y > 0.5) discard;
            gl_FragColor = vec4(uColor, uAlpha);
          } `}
      />
    </mesh>
  );
}

/* ================= Floating dust motes ================= */
function DustMotes({ pRef }) {
  const ref = useRef();
  const uniforms = useMemo(() => ({ uTime: { value: 0 }, uTint: { value: new THREE.Color("#ff4fd8") } }), []);
  const geo = useMemo(() => {
    const count = 900;
    const g = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const seeds = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      positions[i * 3 + 0] = (Math.random() - 0.5) * 60;
      positions[i * 3 + 1] = Math.random() * 12 + 0.5;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 50 - 5;
      seeds[i] = Math.random();
    }
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    g.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));
    return g;
  }, []);
  useFrame((s) => {
    uniforms.uTime.value = s.clock.elapsedTime;
    lerpPalette(STARCOL, pRef.current, uniforms.uTint.value);
  });
  return (
    <points ref={ref} geometry={geo}>
      <shaderMaterial
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        vertexShader={`
          attribute float aSeed;
          uniform float uTime;
          varying float vSeed;
          void main(){
            vSeed = aSeed;
            vec3 p = position;
            p.y += sin(uTime*0.3 + aSeed*20.0) * 0.6;
            p.x += cos(uTime*0.22 + aSeed*30.0) * 0.4;
            vec4 mv = modelViewMatrix * vec4(p, 1.0);
            gl_PointSize = mix(1.4, 3.4, aSeed) * (140.0 / -mv.z);
            gl_Position = projectionMatrix * mv;
          } `}
        fragmentShader={`
          uniform vec3 uTint;
          uniform float uTime;
          varying float vSeed;
          void main(){
            vec2 c = gl_PointCoord - 0.5;
            float d = length(c);
            if (d > 0.5) discard;
            float a = smoothstep(0.5, 0.0, d);
            float tw = 0.55 + 0.45 * sin(uTime*2.0 + vSeed*50.0);
            gl_FragColor = vec4(uTint * (0.6 + 0.4*vSeed), a * tw * 0.85);
          } `}
      />
    </points>
  );
}

/* ================= Sun (glows via bloom in sunset state) ================= */
function Sun({ pRef }) {
  const ref = useRef();
  useFrame(() => {
    if (ref.current) ref.current.material.opacity = lerpScalar(SUN_OPACITY, pRef.current);
  });
  return (
    <mesh ref={ref} position={[0, 1.2, -120]}>
      <circleGeometry args={[16, 48]} />
      <meshBasicMaterial color="#fff6d0" transparent opacity={0} toneMapped={false} />
    </mesh>
  );
}

/* ================= Desert terrain with undulating hills ================= */
function Terrain({ pRef }) {
  const mat = useRef();
  const geo = useMemo(() => new THREE.PlaneGeometry(500, 500, 160, 160), []);
  useEffect(() => {
    const m = mat.current;
    if (!m) return;
    m.onBeforeCompile = (sh) => {
      sh.vertexShader = sh.vertexShader.replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
         float h = sin(position.x*0.12)*3.2 + cos(position.y*0.10)*3.0
                 + sin((position.x*0.3 + position.y*0.22))*0.8
                 + sin(position.x*0.04 + position.y*0.05)*6.0;
         transformed.z += h;`
      );
    };
    m.needsUpdate = true;
  }, []);
  useFrame(() => {
    if (mat.current) lerpPalette(TERRAIN, pRef.current, mat.current.color);
  });
  return (
    <mesh geometry={geo} rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.5, -40]}>
      <meshStandardMaterial ref={mat} color="#1a0620" roughness={0.9} metalness={0.1} />
    </mesh>
  );
}

/* ================= Planet / cool moon (was Blob) ================= */
function Planet({ pRef }) {
  const group = useRef();
  const surfaceMat = useRef();
  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uTint: { value: new THREE.Color("#ff4fd8") },
      uCore: { value: new THREE.Color("#e8d0ff") },
    }),
    []
  );
  useFrame((s) => {
    uniforms.uTime.value = s.clock.elapsedTime;
    lerpPalette(STARCOL, pRef.current, uniforms.uTint.value);
    if (group.current) {
      // slow orbital drift + spin
      group.current.rotation.y = s.clock.elapsedTime * 0.06;
      group.current.position.y = 3.4 + Math.sin(s.clock.elapsedTime * 0.35) * 0.25;
    }
  });
  return (
    <group ref={group} position={[-6.5, 3.4, 1.5]}>
      {/* rocky planetary body — procedural craters + terminator */}
      <mesh>
        <sphereGeometry args={[2.1, 128, 128]} />
        <shaderMaterial
          ref={surfaceMat}
          uniforms={uniforms}
          vertexShader={`
            varying vec3 vN;
            varying vec3 vP;
            varying vec3 vView;
            void main(){
              vN = normalize(normalMatrix * normal);
              vP = position;
              vec4 mv = modelViewMatrix * vec4(position, 1.0);
              vView = -mv.xyz;
              gl_Position = projectionMatrix * mv;
            } `}
          fragmentShader={`
            varying vec3 vN;
            varying vec3 vP;
            varying vec3 vView;
            uniform float uTime;
            uniform vec3 uTint;
            uniform vec3 uCore;
            float hash(vec3 p){ return fract(sin(dot(p, vec3(127.1, 311.7, 74.7)))*43758.5453123); }
            float noise(vec3 p){
              vec3 i = floor(p); vec3 f = fract(p);
              f = f*f*(3.0-2.0*f);
              float n = mix(mix(mix(hash(i), hash(i+vec3(1,0,0)), f.x),
                                mix(hash(i+vec3(0,1,0)), hash(i+vec3(1,1,0)), f.x), f.y),
                            mix(mix(hash(i+vec3(0,0,1)), hash(i+vec3(1,0,1)), f.x),
                                mix(hash(i+vec3(0,1,1)), hash(i+vec3(1,1,1)), f.x), f.y), f.z);
              return n;
            }
            float fbm(vec3 p){
              float v = 0.0; float a = 0.5;
              for (int i=0; i<6; i++){ v += a * noise(p); p *= 2.03; a *= 0.5; }
              return v;
            }
            void main(){
              vec3 p = normalize(vP);
              // terrain / craters — two octaves of fbm, sharp ridges
              float base = fbm(p * 3.8);
              float craters = fbm(p * 12.0 + 4.2);
              craters = smoothstep(0.35, 0.7, craters);
              float ridges = 1.0 - abs(fbm(p * 6.5) * 2.0 - 1.0);
              float land = base * 0.65 + craters * 0.25 + ridges * 0.12;
              // colour: dark rock -> mid tint -> highlands
              vec3 rock = mix(vec3(0.06, 0.02, 0.08), uTint * 0.35, land);
              rock = mix(rock, uCore, smoothstep(0.72, 0.95, land) * 0.6);
              // fake terminator (day/night light dir)
              vec3 L = normalize(vec3(0.7, 0.35, 0.6));
              float d = clamp(dot(vN, L), 0.0, 1.0);
              float night = pow(1.0 - d, 1.6);
              vec3 col = rock * (0.35 + 0.9*d);
              // emissive city lights on the night side
              float lights = smoothstep(0.72, 0.98, fbm(p * 40.0 + uTime*0.05));
              col += uTint * lights * night * 1.8;
              // rim atmosphere glow (Fresnel)
              float fres = pow(1.0 - clamp(dot(normalize(vN), normalize(vView)),0.0,1.0), 2.6);
              col += uTint * fres * 1.3;
              gl_FragColor = vec4(col, 1.0);
            } `}
        />
      </mesh>
      {/* soft outer atmosphere halo */}
      <mesh scale={1.14}>
        <sphereGeometry args={[2.1, 48, 48]} />
        <shaderMaterial
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          side={THREE.BackSide}
          uniforms={uniforms}
          vertexShader={`varying vec3 vN; varying vec3 vView;
            void main(){ vN = normalize(normalMatrix * normal); vec4 mv = modelViewMatrix * vec4(position,1.0); vView = -mv.xyz; gl_Position = projectionMatrix * mv;} `}
          fragmentShader={`varying vec3 vN; varying vec3 vView; uniform vec3 uTint;
            void main(){ float fres = pow(1.0 - clamp(dot(normalize(vN), normalize(vView)),0.0,1.0), 2.0);
              gl_FragColor = vec4(uTint, fres*0.65); } `}
        />
      </mesh>
      {/* tilted iridescent ring */}
      <mesh rotation={[Math.PI * 0.36, 0.5, 0.2]}>
        <ringGeometry args={[2.9, 3.9, 128]} />
        <shaderMaterial
          transparent
          depthWrite={false}
          side={THREE.DoubleSide}
          blending={THREE.AdditiveBlending}
          uniforms={uniforms}
          vertexShader={`varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);} `}
          fragmentShader={`varying vec2 vUv; uniform vec3 uTint; uniform float uTime;
            float hash(float n){ return fract(sin(n*127.1)*43758.5453);}
            void main(){
              float r = length(vUv - 0.5) * 2.0;
              // gaps
              float bands = 0.5 + 0.5*sin(r*70.0 + uTime*0.4);
              float dust = smoothstep(0.15, 0.9, bands);
              float edge = smoothstep(1.0, 0.55, r) * smoothstep(0.0, 0.05, r-0.02);
              gl_FragColor = vec4(uTint, dust * edge * 0.75);
            } `}
        />
      </mesh>
    </group>
  );
}

/* ================= Neon tunnel rings (state 2) ================= */
function Tunnel({ pRef }) {
  const group = useRef();
  const rings = useMemo(() => new Array(16).fill(0), []);
  useFrame((s) => {
    if (!group.current) return;
    // visible only around state 2 (p ~ 0.2 - 0.42)
    const vis = THREE.MathUtils.clamp(1 - Math.abs(pRef.current - 0.3) / 0.13, 0, 1);
    group.current.visible = vis > 0.02;
    group.current.children.forEach((m, i) => {
      const speed = 8;
      let z = ((s.clock.elapsedTime * speed + i * 3) % 48) - 44; // travel toward camera
      m.position.z = -z; // rings ahead of head moving forward
      const puls = 1 + Math.sin(s.clock.elapsedTime * 3 + i) * 0.12;
      m.scale.setScalar(puls);
      m.material.opacity = vis * THREE.MathUtils.clamp(1 - Math.abs(z) / 44, 0, 1) * 0.9;
    });
  });
  return (
    <group ref={group} position={[0, 3.15, 0]}>
      {rings.map((_, i) => (
        <mesh key={i} rotation={[0, 0, 0]}>
          <torusGeometry args={[2.4 + (i % 3) * 0.15, 0.06, 12, 64]} />
          <meshBasicMaterial color={i % 2 ? "#ff2f9e" : "#b04dff"} transparent opacity={0} toneMapped={false} blending={THREE.AdditiveBlending} />
        </mesh>
      ))}
    </group>
  );
}

/* ================= Central figure (male droid — faces the planet) ================= */
const PLANET_POS = new THREE.Vector3(-6.5, 3.4, 1.5);

// Module-level singleton loader: guarantees the .glb only downloads once,
// even under React strict-mode's double-mounting. Uses raw XHR to bypass the
// third-party session-recorder that monkey-patches window.fetch and breaks
// binary ArrayBuffer downloads in this preview environment.
let _figurePromise = null;
function loadFigureOnce() {
  if (_figurePromise) return _figurePromise;
  _figurePromise = new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", "/male_avatar.glb", true);
    xhr.responseType = "arraybuffer";
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) return reject(new Error(`HTTP ${xhr.status}`));
      const loader = new GLTFLoader();
      loader.parse(xhr.response, "", (gltf) => resolve(gltf.scene), reject);
    };
    xhr.onerror = () => reject(new Error("XHR network error"));
    xhr.send();
  }).catch((err) => {
    _figurePromise = null; // allow retry on next mount
    throw err;
  });
  return _figurePromise;
}

function Figure({ pRef }) {
  const [scene, setScene] = useState(null);
  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uMinY: { value: 0 },
      uMaxY: { value: 1 },
      uHeadY: { value: 1 },
      uStar: { value: new THREE.Color("#ff4fd8") },
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    loadFigureOnce()
      .then((source) => {
        if (cancelled) return;
        try {
          const model = cloneSkeleton(source);

        model.scale.setScalar(1.5);
        model.updateMatrixWorld(true);

        // 1) pose arms first (in bind-pose, before any model rotation).
        // CC_Base rig uses X-axis rotation on the shoulder to swing the arm along the body,
        // and both L/R arms rotate the SAME direction (mirror of the pattern for L/R).
        const armAngle = 1.35;
        model.traverse((o) => {
          const nm = o.name || "";
          if (nm.includes("Twist") || nm.includes("correct")) return;
          if (nm.startsWith("CC_Base_L_Upperarm_")) {
            o.rotation.z = -armAngle;
          } else if (nm.startsWith("CC_Base_R_Upperarm_")) {
            o.rotation.z = armAngle;
          }
        });

        // 2) face the CAMERA (default forward is +Z so rotation.y = 0)
        model.rotation.y = 0;
        model.updateMatrixWorld(true);

        // plant feet on the ground (y = 0)
        model.updateMatrixWorld(true);
        const box = new THREE.Box3();
        model.traverse((o) => {
          if (!(o.isSkinnedMesh || o.isMesh) || !o.geometry) return;
          if ((o.name || "").match(/Eye|Cornea|Tongue|Teeth|Lash/i)) return;
          o.geometry.computeBoundingBox();
          const bb = o.geometry.boundingBox.clone().applyMatrix4(o.matrixWorld);
          box.union(bb);
        });
        if (isFinite(box.min.y)) {
          model.position.y = -box.min.y;
          const c = new THREE.Vector3();
          box.getCenter(c);
          model.position.x = -c.x;
        }

        // cosmic star + head-ring shader injection — compute LOCAL-space Y range across all body meshes
        // (vLocalP in the shader is raw local vertex position, not skinned world-space)
        const localBox = new THREE.Box3();
        model.traverse((o) => {
          if (!o.isMesh || !o.geometry) return;
          if ((o.name || "").match(/Eye|Cornea|Tongue|Teeth|Lash/i)) return;
          o.geometry.computeBoundingBox();
          localBox.union(o.geometry.boundingBox);
        });
        uniforms.uMinY.value = localBox.min.y;
        uniforms.uMaxY.value = localBox.max.y;
        model.traverse((o) => {
          if (!o.isMesh || !o.geometry) return;
          if ((o.name || "").match(/Eye|Cornea|Tongue|Teeth|Lash/i)) {
            o.visible = false;
            return;
          }
          o.frustumCulled = false;
          const mats = Array.isArray(o.material) ? o.material : [o.material];
          mats.forEach((m) => {
            m.color = new THREE.Color("#050010");
            m.emissive = new THREE.Color("#000000");
            m.metalness = 0.2;
            m.roughness = 0.6;
            m.onBeforeCompile = (sh) => {
              sh.uniforms.uTime = uniforms.uTime;
              sh.uniforms.uMinY = uniforms.uMinY;
              sh.uniforms.uMaxY = uniforms.uMaxY;
              sh.vertexShader = sh.vertexShader
                .replace("#include <common>", "#include <common>\nvarying vec3 vLocalP;\nvarying vec3 vLocalN;")
                .replace(
                  "#include <begin_vertex>",
                  "#include <begin_vertex>\nvLocalP = position;\nvLocalN = normal;"
                );
              sh.fragmentShader = sh.fragmentShader
                .replace(
                  "#include <common>",
                  `#include <common>
                   varying vec3 vLocalP;
                   varying vec3 vLocalN;
                   uniform float uTime, uMinY, uMaxY;
                   float hash3(vec3 p){ p = fract(p * 0.3183099 + vec3(0.1)); p *= 17.0; return fract(p.x*p.y*p.z*(p.x+p.y+p.z)); }`
                )
                .replace(
                  "#include <emissivemap_fragment>",
                  `#include <emissivemap_fragment>
                   // 1) cosmic starfield across the whole body
                   float stars = hash3(floor(vLocalP * 40.0 + vec3(0.0, uTime * 0.5, 0.0)));
                   float starMask = step(0.98, stars);
                   float twinkle = 0.5 + 0.5 * sin(uTime*5.0 + stars*10.0);
                   vec3 starGlow = vec3(0.9, 0.2, 1.0) * starMask * twinkle * 2.0;

                   // 2) concentric rings on the BACK of the head (top of the model, normal facing -Z in world)
                   float yN = clamp((vLocalP.y - uMinY)/max(uMaxY-uMinY,0.0001), 0.0, 1.0);
                   float headMask = smoothstep(0.86, 0.94, yN);
                   float backMask = smoothstep(0.05, -0.15, vLocalN.z);
                   vec2 headUv = vec2(vLocalP.x, vLocalP.y - (uMinY + (uMaxY-uMinY)*0.90));
                   float rDist = length(headUv);
                   float rings = sin(rDist * 60.0 - uTime * 8.0);
                   rings = smoothstep(0.7, 0.8, rings);
                   vec3 ringGlow = vec3(0.6, 0.0, 1.0) * rings * 3.0 * headMask * backMask;

                   // 3) fresnel rim
                   vec3 nrm = normalize(normal);
                   vec3 vd = normalize(vViewPosition);
                   float fres = pow(1.0 - max(dot(nrm, vd), 0.0), 4.0);
                   vec3 rim = vec3(0.8, 0.1, 0.9) * fres * 0.55;

                   totalEmissiveRadiance += starGlow + ringGlow + rim;`
                );
            };
            m.needsUpdate = true;
          });
        });

        setScene(model);
        } catch (e) {
          console.error("[Figure] post-parse error:", e);
        }
      })
      .catch((err) => console.error("male_avatar.glb load failed:", err));
    return () => {
      cancelled = true;
    };
  }, [uniforms]);

  useFrame((s) => {
    uniforms.uTime.value = s.clock.elapsedTime;
    lerpPalette(STARCOL, pRef.current, uniforms.uStar.value);
  });

  if (!scene) return null;
  return <primitive object={scene} />;
}

/* ================= Rig: camera (360° scroll-driven orbit) + lights + palette + post ================= */
function Rig({ pRef }) {
  const { scene, camera } = useThree();
  const look = useMemo(() => new THREE.Vector3(), []);
  const fog = useMemo(() => new THREE.Fog("#8a3a90", 22, 180), []);
  const caOffset = useMemo(() => new THREE.Vector2(0.0009, 0.0012), []);

  useEffect(() => {
    scene.fog = fog;
  }, [scene, fog]);

  useFrame(() => {
    const p = THREE.MathUtils.clamp(pRef.current, 0, 1);
    // interpolate radius / height / lookY through the 5 SIDEWAVE state keyframes
    const scaled = p * (ORBIT_KF.length - 1);
    const i = Math.min(Math.floor(scaled), ORBIT_KF.length - 2);
    const raw = scaled - i;
    const t = raw * raw * (3 - 2 * raw); // smoothstep — matches the "power2.inOut" feel from the old GSAP
    const a = ORBIT_KF[i];
    const b = ORBIT_KF[i + 1];
    const r  = a.r  + (b.r  - a.r)  * t;
    const py = a.py + (b.py - a.py) * t;
    const ly = a.ly + (b.ly - a.ly) * t;
    // 360° yaw driven purely by scroll
    const yaw = p * Math.PI * 2 * ORBIT_TURNS;
    camera.position.set(Math.sin(yaw) * r, py, Math.cos(yaw) * r);
    look.set(0, ly, 0);
    camera.lookAt(look);
    lerpPalette(FOGCOL, p, fog.color);
  });

  return (
    <>
      <ambientLight intensity={1.35} />
      <hemisphereLight args={["#ffd0e8", "#3a1e5a", 0.9]} />
      <directionalLight position={[6, 10, -8]} intensity={2.2} color="#ffe0f0" />
      <directionalLight position={[-8, 6, 4]} intensity={1.4} color="#ff8ad0" />
      <pointLight position={[-6.5, 4, 3]} intensity={38} distance={26} color="#ff6fd0" />
      <pointLight position={[6, 5, 4]} intensity={22} distance={24} color="#b06fff" />

      <SkyDome pRef={pRef} />
      <Sun pRef={pRef} />
      <DistantMountains pRef={pRef} />
      <Terrain pRef={pRef} />
      <Tunnel pRef={pRef} />
      <Planet pRef={pRef} />
      <DustMotes pRef={pRef} />
      <Figure pRef={pRef} />

      <EffectComposer>
        <Bloom intensity={1.55} luminanceThreshold={0.35} luminanceSmoothing={0.5} mipmapBlur radius={0.9} />
        <ChromaticAberration blendFunction={BlendFunction.NORMAL} offset={caOffset} radialModulation={false} modulationOffset={0} />
        <Noise premultiply blendFunction={BlendFunction.OVERLAY} opacity={0.22} />
        <Vignette eskil={false} offset={0.35} darkness={0.55} />
      </EffectComposer>
    </>
  );
}

/* ================= Page (fixed UI overlay + scroll) ================= */
export default function Experience() {
  const pRef = useRef(0);
  const [p, setP] = useState(0);
  const [menu, setMenu] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [cookie, setCookie] = useState(false);

  const lenis = useLenis((l) => {
    const v = THREE.MathUtils.clamp(l.progress || 0, 0, 1);
    pRef.current = v;
    setP(v);
  });

  useEffect(() => {
    const t = setTimeout(() => setCookie(true), 1500);
    return () => clearTimeout(t);
  }, []);

  const state = Math.min(4, Math.floor(p / 0.2)); // 0..4
  const bump = (center, width) => THREE.MathUtils.clamp(1 - Math.abs(p - center) / width, 0, 1);

  // mission data readouts (monospace) update with scroll
  const coordX = (48.211 + p * 12.44).toFixed(3);
  const coordY = (13.004 + p * 8.71).toFixed(3);
  const missionLines = [
    "THE PRODUCTION OF RADIANCE",
    "ANALYSIS OF ACTIVE COMPLEX",
    `SEQ ${String(Math.floor(p * 9999)).padStart(4, "0")} / 9999`,
    `LAT ${coordX}  LON ${coordY}`,
    `STATE 0${state + 1} · ${["INTRO", "TUNNEL", "SUNSET", "REVEAL", "INDEX"][state]}`,
  ];

  const jumpTo = (frac) => {
    const max = document.body.scrollHeight - window.innerHeight;
    if (lenis) lenis.scrollTo(max * frac);
    else window.scrollTo({ top: max * frac, behavior: "smooth" });
    setMenu(false);
  };

  return (
    <div className="sw-root" data-testid="experience-root">
      <div className="sw-canvas">
        <Canvas dpr={[1, 2]} camera={{ position: [0, 3.4, 15], fov: 45 }} gl={{ antialias: true }} onCreated={() => setLoaded(true)}>
          <Rig pRef={pRef} />
        </Canvas>
      </div>

      {!loaded && <div className="sw-loader" data-testid="experience-loader">PRESTIGE WELLNESS CO<span>initialising</span></div>}
      <div className="sw-grain" />

      {/* header */}
      <header className="sw-header" data-testid="sw-header">
        <Link to="/experience" className="sw-brand" data-testid="sw-brand">PRESTIGE WELLNESS CO</Link>
        <button className="sw-menu-trigger" data-testid="sw-menu-btn" onClick={() => setMenu(true)}>MENU</button>
      </header>

      {/* left mission data */}
      <aside className="sw-mission" data-testid="sw-mission">
        <p className="sw-mission-title">[ MISSION ]</p>
        {missionLines.map((l, i) => (
          <p key={i} className="sw-data-line" data-testid={`sw-data-${i}`}>{l}</p>
        ))}
      </aside>

      {/* right red ribbon */}
      <div className="sw-ribbon" data-testid="sw-ribbon"><span>SITE OF THE DAY · W.</span></div>

      {/* STATE 1 — intro semi-transparent title */}
      <div className="sw-title-intro" style={{ opacity: bump(0.08, 0.14) }} aria-hidden>A DIGITAL RITUAL</div>

      {/* STATE 4 — glitch text reveal */}
      <div className="sw-glitch-wrap" style={{ opacity: bump(0.7, 0.09) }} aria-hidden>
        <span className="sw-glitch" data-text="ILLUMINATED">ILLUMINATED</span>
      </div>

      {/* STATE 5 — product carousel across the torso */}
      <div className="sw-carousel-wrap" style={{ opacity: bump(0.9, 0.11) }} aria-hidden>
        <div className="sw-carousel-track">
          <div className="sw-carousel-cursor" />
        </div>
        <div className="sw-carousel">
          <div className="sw-carousel-inner">
            {[...CAROUSEL, ...CAROUSEL, ...CAROUSEL].map((w, i) => (
              <span key={i} className="sw-carousel-item">{w}<i className="sw-sep">✦</i></span>
            ))}
          </div>
        </div>
      </div>

      {/* menu overlay */}
      {menu && (
        <div className="sw-menu" data-testid="sw-menu">
          <button className="sw-menu-close" data-testid="sw-menu-close" onClick={() => setMenu(false)}>CLOSE</button>
          {CAROUSEL.map((w, i) => (
            <button key={w} className="sw-menu-link" style={{ animationDelay: `${i * 0.07}s` }}
              onClick={() => jumpTo(0.2 + i * 0.2)}>{w}</button>
          ))}
          <Link to="/" className="sw-menu-link" style={{ animationDelay: "0.35s" }}>MAIN STORE</Link>
        </div>
      )}

      {/* footer cookie banner */}
      {cookie && (
        <footer className="sw-cookie" data-testid="sw-cookie">
          <p className="sw-cookie-text">WE USE COOKIES TO IMPROVE YOUR EXPERIENCE AND COLLECT ANALYTICAL DATA.</p>
          <div className="sw-cookie-btns">
            <button data-testid="sw-accept" onClick={() => setCookie(false)}>ACCEPT ALL</button>
            <button data-testid="sw-reject" onClick={() => setCookie(false)}>REJECT ALL</button>
          </div>
          <div className="sw-cookie-links">
            <button onClick={() => setCookie(false)}>MANAGE PREFERENCES</button>
            <span>·</span>
            <button onClick={() => setCookie(false)}>COOKIE POLICY</button>
          </div>
        </footer>
      )}

      {/* tall scroll driver */}
      <div className="sw-scroll"><div className="sw-spacer" /></div>
    </div>
  );
}

// no preload — Figure manages GLTFLoader manually
